/**
 * Library-free i18n shim for the unsaved-changes guard.
 *
 * The app doesn't yet ship with `react-i18next` or similar. To keep this
 * isolated and trivially swappable later, we maintain a tiny string registry
 * with English defaults that any caller can override at runtime
 * (e.g. when bootstrapping a locale).
 *
 * Future migration: replace `t()` with `useTranslation().t` and load the
 * default keys from a JSON locale bundle. Call sites stay the same.
 */

export type UnsavedChangesGuardStringKey =
  | "discardToast"
  | "discardDialogTitle"
  | "discardDialogDescription"
  | "discardDialogConfirm"
  | "discardDialogCancel";

export const DEFAULT_UNSAVED_CHANGES_STRINGS: Record<
  UnsavedChangesGuardStringKey,
  string
> = {
  discardToast: "Changes discarded — reverted to last saved configuration",
  discardDialogTitle: "Discard unsaved changes?",
  discardDialogDescription:
    "You have unsaved changes. If you leave now, your changes will be lost.",
  discardDialogConfirm: "Discard",
  discardDialogCancel: "Keep editing",
};

const overrides: Partial<Record<UnsavedChangesGuardStringKey, string>> = {};

/**
 * Hard cap on any single localized string. Bundles loaded from untrusted
 * JSON sources (remote config, partner-supplied translations) occasionally
 * ship pathologically long values that would blow out dialog layout or
 * produce unreadable toasts. Anything longer is rejected and the registry
 * falls back to the English default.
 */
export const UNSAVED_CHANGES_MAX_STRING_LENGTH = 500;

/**
 * Returns true when `value` is a non-empty string within the size cap.
 * Used as the gate for every override entry so wrong-type or oversized
 * values from untyped JSON bundles never reach the UI.
 */
function isAcceptableOverrideValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= UNSAVED_CHANGES_MAX_STRING_LENGTH
  );
}

/**
 * Per-surface presets, keyed by the `surface` string passed to
 * `useUnsavedChangesGuard`. Lets product teams ship dialog-specific copy
 * (e.g. "Discard PPPC changes?") without threading `i18n` props through
 * intermediate components.
 */
const surfacePresets: Record<
  string,
  Partial<Record<UnsavedChangesGuardStringKey, string>>
> = {};

/**
 * Bulk-set localized strings, typically once at app bootstrap when the
 * active locale is known. Keys you omit fall back to the English defaults.
 */
export function configureUnsavedChangesStrings(
  next: Partial<Record<UnsavedChangesGuardStringKey, string>>,
): void {
  for (const k of Object.keys(next) as UnsavedChangesGuardStringKey[]) {
    const v = next[k];
    if (isAcceptableOverrideValue(v)) overrides[k] = v;
  }
}

/**
 * Register (or extend) a per-surface preset. Calling this multiple times
 * for the same surface merges new keys on top of any previously registered
 * ones, so locale bootstrap and feature modules can contribute independently.
 *
 * @example
 * registerUnsavedChangesSurfacePreset("PrivacyPermissionsDialog", {
 *   discardDialogTitle: "Discard PPPC changes?",
 *   discardToast: "PPPC changes discarded",
 * });
 */
export function registerUnsavedChangesSurfacePreset(
  surface: string,
  preset: Partial<Record<UnsavedChangesGuardStringKey, string>>,
): void {
  const existing = surfacePresets[surface] ?? {};
  const merged: Partial<Record<UnsavedChangesGuardStringKey, string>> = {
    ...existing,
  };
  for (const k of Object.keys(preset) as UnsavedChangesGuardStringKey[]) {
    const v = preset[k];
    if (isAcceptableOverrideValue(v)) merged[k] = v;
  }
  surfacePresets[surface] = merged;
}

/** Inspect the active preset for a surface (read-only copy). Useful in tests. */
export function getUnsavedChangesSurfacePreset(
  surface: string,
): Partial<Record<UnsavedChangesGuardStringKey, string>> {
  return { ...(surfacePresets[surface] ?? {}) };
}

/** Resets all overrides — intended for tests / locale switches. */
export function resetUnsavedChangesStrings(): void {
  for (const k of Object.keys(overrides)) {
    delete overrides[k as UnsavedChangesGuardStringKey];
  }
  for (const s of Object.keys(surfacePresets)) {
    delete surfacePresets[s];
  }
}

/**
 * Last-resort fallback returned when a caller asks for a key that is not in
 * {@link DEFAULT_UNSAVED_CHANGES_STRINGS} and no override supplies it. Kept as
 * an empty string so callers that render the value directly (toasts, dialog
 * labels) never accidentally render the literal text "undefined".
 *
 * In dev we also log a warning so missing-key bugs surface early instead of
 * silently shipping blank UI.
 */
export const UNSAVED_CHANGES_MISSING_KEY_FALLBACK = "";

/**
 * Resolve a string by key. Resolution priority (highest first):
 *   1. Per-call `callOverrides` (e.g. the hook/dialog `i18n` prop)
 *   2. Per-surface preset registered via
 *      {@link registerUnsavedChangesSurfacePreset}
 *   3. Global overrides set via {@link configureUnsavedChangesStrings}
 *   4. English defaults in {@link DEFAULT_UNSAVED_CHANGES_STRINGS}
 *   5. {@link UNSAVED_CHANGES_MISSING_KEY_FALLBACK} (empty string) — only hit
 *      when the requested key is unknown (e.g. came in via an untyped JSON
 *      locale bundle). Guarantees the return type is always a string.
 */
export function tUnsavedChanges(
  key: UnsavedChangesGuardStringKey,
  callOverrides?: Partial<Record<UnsavedChangesGuardStringKey, string>>,
  surface?: string,
): string {
  const callValue = callOverrides?.[key];
  if (typeof callValue === "string") return callValue;
  if (surface) {
    const surfaceValue = surfacePresets[surface]?.[key];
    if (typeof surfaceValue === "string") return surfaceValue;
  }
  const globalValue = overrides[key];
  if (typeof globalValue === "string") return globalValue;

  const defaultValue = DEFAULT_UNSAVED_CHANGES_STRINGS[key];
  if (typeof defaultValue === "string") return defaultValue;

  // Unknown key path: never return undefined. Warn loudly in dev so the
  // missing entry gets added to DEFAULT_UNSAVED_CHANGES_STRINGS, but stay
  // silent in production to avoid console noise for end users.
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV !== "production"
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[unsavedChangesStrings] Unknown key "${String(
        key,
      )}" — returning empty string. Add it to DEFAULT_UNSAVED_CHANGES_STRINGS.`,
    );
  }
  return UNSAVED_CHANGES_MISSING_KEY_FALLBACK;
}
